// Currency formatting (USD only - maintained for compatibility)
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,  
  }).format(amount || 0);
};

// Dual currency formatting functions
export const formatUSD = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

export const formatVES = (amount) => {
  return new Intl.NumberFormat('es-VE', {
    style: 'currency',
    currency: 'VES',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

export const formatDualCurrency = (usdAmount, exchangeRate = 36.50, options = {}) => {
  const {
    showBoth = true,
    primary = 'USD',
    compact = false,
    separator = ' / '
  } = options;

  const vesAmount = usdAmount * exchangeRate;
  
  if (!showBoth) {
    return primary === 'USD' ? formatUSD(usdAmount) : formatVES(vesAmount);
  }

  if (compact) {
    return primary === 'USD' 
      ? `${formatUSD(usdAmount)}${separator}Bs. ${vesAmount.toFixed(0)}`
      : `Bs. ${vesAmount.toFixed(2)}${separator}${formatUSD(usdAmount)}`;
  }

  return primary === 'USD'
    ? `${formatUSD(usdAmount)}${separator}${formatVES(vesAmount)}`
    : `${formatVES(vesAmount)}${separator}${formatUSD(usdAmount)}`;
};

// Number formatting
export const formatNumber = (number) => {
  return new Intl.NumberFormat('es-ES').format(number || 0);
};

// Date formatting
export const formatDate = (date) => {
  if (!date) return '';
  
  let dateObj;
  
  // Handle date string in YYYY-MM-DD format carefully to avoid timezone issues
  if (typeof date === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      // Parse YYYY-MM-DD directly to avoid timezone conversion
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day); // month is 0-based
    } else {
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }
  
  // Double-check for invalid date
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  // Use local date components to format to avoid timezone issues
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

// Convert date to YYYY-MM-DD string without timezone conversion
export const toLocalDateString = (date) => {
  if (!date) return '';
  
  let dateObj;
  
  // Handle different date input types more carefully
  if (typeof date === 'string') {
    // If it's already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    // Parse string dates carefully to avoid timezone issues
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return '';
  }
  
  // Double-check for invalid date
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  // Use local date components to avoid timezone conversion
  // Force the use of local timezone components
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

export const formatDateLong = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(dateObj);
};

// Percentage formatting
export const formatPercentage = (value, decimals = 1) => {
  return `${(value || 0).toFixed(decimals)}%`;
};

// Expense category translations
export const getCategoryLabel = (category) => {
  const categories = {
    operativo: 'Operativos',
    personal: 'Personal',
    marketing: 'Marketing',
    mantenimiento: 'Mantenimiento',
    seguros: 'Seguros',
    servicios: 'Servicios',
    sueldos_directivos: 'Sueldos a directivos',
    compras_inventario: 'Compras de inventario',
    pagos_proveedores: 'Pagos a proveedores',
    creditos_bancarios: 'Créditos bancarios',
    otros: 'Otros',
  };
  
  return categories[category] || category;
};

// Color utilities for charts
export const getChartColors = () => {
  return [
    '#3B82F6', // Blue
    '#EF4444', // Red
    '#10B981', // Green
    '#F59E0B', // Yellow
    '#8B5CF6', // Purple
    '#06B6D4', // Cyan
    '#F97316', // Orange
    '#84CC16', // Lime
    '#EC4899', // Pink
    '#6B7280', // Gray
  ];
};

// Status utilities
export const getStatusColor = (status) => {
  const colors = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800',
  };
  
  return colors[status] || 'bg-gray-100 text-gray-800';
};

// Trend indicators
export const getTrendIcon = (value) => {
  if (value > 0) {
    return { icon: '↗️', class: 'text-green-600', label: 'Incremento' };
  } else if (value < 0) {
    return { icon: '↘️', class: 'text-red-600', label: 'Disminución' };
  } else {
    return { icon: '➡️', class: 'text-gray-600', label: 'Sin cambio' };
  }
};

// Date range utilities
export const getDateRangeLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return 'Período completo';
  
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  
  if (start === end) return start;
  return `${start} - ${end}`;
};

// Summary text generators
export const generateSalesSummary = (totalSales, salesCount) => {
  return `${formatCurrency(totalSales)} en ${salesCount} venta${salesCount !== 1 ? 's' : ''}`;
};

export const generateExpensesSummary = (totalExpenses, expensesCount) => {
  return `${formatCurrency(totalExpenses)} en ${expensesCount} gasto${expensesCount !== 1 ? 's' : ''}`;
};

// Validation utilities
export const isValidAmount = (amount) => {
  return !isNaN(amount) && amount > 0;
};

export const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date.getTime());
};

// Data export utilities
export const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) return;
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default {
  formatCurrency,
  formatNumber,
  formatDate,
  formatDateLong,
  toLocalDateString,
  formatPercentage,
  getCategoryLabel,
  getChartColors,
  getStatusColor,
  getTrendIcon,
  getDateRangeLabel,
  generateSalesSummary,
  generateExpensesSummary,
  isValidAmount,
  isValidDate,
  exportToCSV,
};

// ── Fechas/horas del servidor ────────────────────────────────────────────────
// El backend guarda timestamps en UTC sin sufijo 'Z' (datetime.utcnow().isoformat()).
// Estas funciones los interpretan como UTC y los muestran en hora de Venezuela.
export const VE_TZ = 'America/Caracas';

export const serverDate = (iso) => {
  if (!iso) return null;
  if (iso instanceof Date) return iso;
  const s = String(iso);
  const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasOffset || !s.includes('T') ? s : s + 'Z');
  return isNaN(d) ? null : d;
};

export const formatDateTimeVE = (iso, opts = {}) => {
  const d = serverDate(iso);
  if (!d) return '—';
  return d.toLocaleString('es-VE', {
    timeZone: VE_TZ,
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    ...opts,
  });
};

// 'YYYY-MM-DD' en hora de Venezuela (para agrupar por día)
export const localDateKey = (iso) => {
  const d = serverDate(iso);
  if (!d) return null;
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: VE_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const get = (t) => parts.find(x => x.type === t).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};

export const todayKeyVE = (offsetDays = 0) => localDateKey(new Date(Date.now() + offsetDays * 86400000));
